/**
 * The keypair a child's device makes for itself, and the match code.
 *
 * WHAT THIS REPLACED. A child's link code used to carry that child's person
 * key, sealed under the code, in a `kb` bundle sitting in the table. That
 * worked, and it meant the code had to be twenty characters: somebody who
 * dumped the table had the wrapped key in hand and could attack it offline,
 * where none of the rate limiting exists.
 *
 * Approval gave us a second moment when a parent is present, and it is the
 * better one to hand a key over in. So:
 *
 *   child's device  --generates-->  RSA-OAEP keypair
 *                                     |         |
 *                   private, non-extractable    public, sent with the request
 *                   never leaves IndexedDB          |
 *                                                   v
 *   parent's device --opens pfw with the family key, wraps the person
 *                     key TO that public key, sends the bytes--> server
 *                                                   |
 *   child's device  <--unwraps with its private key-+
 *
 * The server stores the wrapped bytes and can open neither half. The code
 * carries nothing, so eight characters is enough, and there is no key
 * material for a child code anywhere in the table.
 *
 * WHY RSA-OAEP RATHER THAN ECDH. The brief allowed either and asked for a
 * reason. RSA-OAEP is one primitive and one call on each side: `wrapKey` with
 * a public key, `unwrapKey` with a private one. ECDH would need an ephemeral
 * keypair, a key agreement, an HKDF step and an AES-GCM wrap with a nonce to
 * manage -- four things to get right, on the path that hands a child's device
 * the key to their own name, to save a few hundred milliseconds once per
 * device and a couple of hundred bytes. The slow part of RSA here is
 * generation, and it happens once, while a child is reading a screen that
 * says "show this to a grown-up".
 *
 * 2048 rather than 4096 because generation on a cheap tablet is the cost
 * anybody actually feels, and what this protects is a key that is itself
 * rotated by removing the person. OAEP with SHA-256, which is the modern
 * default and the only padding worth using.
 */

import { fromBase64, toBase64 } from './familyKey';

const DB_NAME = 'valley-child';
const STORE = 'keys';
const PAIR_KEY = 'device-keypair';

const ALGORITHM: RsaHashedKeyGenParams = {
  name: 'RSA-OAEP',
  modulusLength: 2048,
  publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
  hash: 'SHA-256',
};

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open storage.'));
  });
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>) {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = work(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('Storage failed.'));
        tx.oncomplete = () => db.close();
      }),
  );
}

/**
 * This device's keypair, made once and kept.
 *
 * PER DEVICE, not per person, which is why it lives beside the sessions
 * rather than inside one. Two siblings sharing a computer each have their own
 * session and their own person key, and both arrived through the same
 * keypair -- a parent approving Susan's request on the family PC is approving
 * that browser, which is the thing they can actually see in front of them.
 *
 * `extractable: false` applies to the PRIVATE key; WebCrypto always leaves a
 * public key exportable, which is what we want, because sending it is its
 * whole job. So the private half cannot be read out by any code on this
 * origin, including this module -- there is no function here that could
 * return it, and `exportKey` on it throws.
 *
 * That is a stronger claim than the family key can make (see familyKey.ts,
 * which has to be extractable because wrapping it is how a parent shares it).
 * Nothing ever wraps this one, so nothing needs it out.
 */
export async function deviceKeypair(): Promise<CryptoKeyPair> {
  const existing = await recall();
  if (existing) return existing;

  const pair = await crypto.subtle.generateKey(ALGORITHM, false, [
    'wrapKey',
    'unwrapKey',
  ]);
  try {
    await run('readwrite', (store) => store.put(pair, PAIR_KEY));
  } catch {
    // A private window, or blocked site data. The pair still works for this
    // page load, so the request can be made and approved -- it just will not
    // survive a reload, and the child types their code again. Better than
    // refusing to let them in at all.
  }
  return pair;
}

async function recall(): Promise<CryptoKeyPair | null> {
  try {
    const found = await run<CryptoKeyPair | undefined>('readonly', (store) =>
      store.get(PAIR_KEY),
    );
    return found ?? null;
  } catch {
    return null;
  }
}

/** The public half, as the base64 SPKI the request carries. */
export async function publicKeyBytes(pair: CryptoKeyPair): Promise<string> {
  return toBase64(await crypto.subtle.exportKey('spki', pair.publicKey));
}

/** The parent's side: a public key to wrap a person key to. */
export async function importPublicKey(spki: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'spki',
    fromBase64(spki) as BufferSource,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    true,
    ['wrapKey'],
  );
}

/**
 * Wrap a person key to a device's public key. Run on the PARENT's device.
 *
 * The person key has to be extractable for this, and it is: `openPersonKey`
 * imports it that way, because being handed on is what a person key is for.
 */
export async function wrapForDevice(
  personKey: CryptoKey,
  devicePublic: CryptoKey,
): Promise<string> {
  const wrapped = await crypto.subtle.wrapKey('raw', personKey, devicePublic, {
    name: 'RSA-OAEP',
  });
  return toBase64(wrapped);
}

/**
 * Open it again. Run on the CHILD's device, with the private key that has
 * never left it.
 *
 * Imported extractable, because the person key goes on to open `pf` and every
 * `rkey` on this child's rows -- and because a key that cannot be re-wrapped
 * would make re-approving this same browser later impossible.
 */
export async function unwrapFromDevice(
  wrapped: string,
  pair: CryptoKeyPair,
): Promise<CryptoKey> {
  return crypto.subtle.unwrapKey(
    'raw',
    fromBase64(wrapped) as BufferSource,
    pair.privateKey,
    { name: 'RSA-OAEP' },
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Sixteen animals, matching MATCH_EMOJI in services/api/valley/linking.py.
 *
 * Both sides derive this from `sha256(devicePub)` over the SAME base64 text,
 * and it is stored nowhere. If these two lists ever drift, the check a parent
 * is asked to make becomes one they always fail -- so the test that asserts
 * they agree is not optional decoration.
 */
const MATCH_EMOJI = [
  '\u{1F43B}', '\u{1F431}', '\u{1F436}', '\u{1F438}', '\u{1F41D}',
  '\u{1F420}', '\u{1F984}', '\u{1F427}', '\u{1F98A}', '\u{1F989}',
  '\u{1F41E}', '\u{1F422}', '\u{1F419}', '\u{1F98B}', '\u{1F995}',
  '\u{1F992}',
] as const;

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * What the child's screen and the parent's card both show.
 *
 * NOT a secret and not a password. It exists so that when two requests are
 * waiting -- a child's real one, and one from somebody who guessed or was
 * given the code -- a parent can tell which is which by looking at the device
 * in front of them. An emoji first because the child holding it may not read
 * yet, and four characters after it because sixteen pictures alone would
 * collide too often to be worth checking.
 */
export async function matchCode(devicePub: string): Promise<string> {
  const digest = new Uint8Array(
    await crypto.subtle.digest('SHA-256', new TextEncoder().encode(devicePub)),
  );
  const emoji = MATCH_EMOJI[digest[0]! % MATCH_EMOJI.length]!;
  const letters = Array.from(digest.slice(1, 5), (byte) => ALPHABET[byte % ALPHABET.length]).join(
    '',
  );
  return `${emoji} ${letters}`;
}
